import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingState } from '@/components/ui/loading-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/contexts/unified-permissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';

import { ChatBubble } from './ChatBubble';
import { ChatInput } from './ChatInput';
import { useChatMessages, useChatOperations } from '../hooks';
import { ADMIN_USER_ID, CHAT_CONFIG } from '../constants';
import type { ChatWindowProps } from '../types';

/**
 * Main chat window component
 * Handles conversation display, message sending, and real-time updates
 */
export const ChatWindow: React.FC<ChatWindowProps> = ({
  isOpen,
  onClose,
  otherUserId = ADMIN_USER_ID,
  otherUserName = 'Support',
}) => {
  const { user } = usePermissions();
  const isMobile = useIsMobile();
  const [newMessage, setNewMessage] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Chat hooks
  const {
    messages,
    loading: messagesLoading,
    error: messagesError,
    addOptimisticMessage,
    removeOptimisticMessage
  } = useChatMessages(otherUserId);
  const { sendMessage, markMessagesAsRead, loading: operationsLoading } = useChatOperations();

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback((immediate = false) => {
    if (messagesEndRef.current) {
      // Use immediate scroll for optimistic messages on mobile
      const behavior = immediate ? 'auto' : 'smooth';
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Use requestAnimationFrame for better mobile performance
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [messages, scrollToBottom]);

  // Mark messages as read when chat opens or new messages arrive
  useEffect(() => {
    if (isOpen && user?.id && otherUserId && messages.length > 0) {
      // Find unread messages from the other user
      const unreadMessages = messages.filter(
        msg => msg.senderUserId === otherUserId && !msg.readAt
      );

      if (unreadMessages.length > 0) {
        // Debounce mark as read to avoid excessive API calls
        const timeoutId = setTimeout(() => {
          markMessagesAsRead(otherUserId);
        }, CHAT_CONFIG.MARK_READ_DEBOUNCE);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [isOpen, user?.id, otherUserId, messages, markMessagesAsRead]);

  // Handle sending a new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user?.id || operationsLoading) {
      return;
    }

    const messageContent = newMessage.trim();
    setNewMessage(''); // Clear input immediately for better UX

    // Add optimistic message immediately
    const tempId = addOptimisticMessage(messageContent, user.id, otherUserId);
    scrollToBottom();

    try {
      const success = await sendMessage(otherUserId, messageContent);
      if (!success) {
        // Remove optimistic message and restore input if send failed
        removeOptimisticMessage(tempId);
        setNewMessage(messageContent);
      }
      // If successful, the real message will come via realtime subscription
      // and replace the optimistic one
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message and restore input
      removeOptimisticMessage(tempId);
      setNewMessage(messageContent);
      toast.error('Failed to send message. Please try again.');
    }
  };

  // Handle dialog close
  const handleClose = () => {
    setNewMessage(''); // Clear any unsent message
    onClose();
  };

  if (!user) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className={cn(
          'flex flex-col h-[80vh] max-h-[600px] p-0',
          isMobile ? 'w-[95vw] h-[90vh]' : 'w-[500px]'
        )}
      >
        {/* Header */}
        <DialogHeader className="flex-shrink-0 p-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Chat with {otherUserName}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Messages area */}
        <div className="flex-1 flex flex-col min-h-0">
          {messagesError && (
            <Alert variant="destructive" className="m-4">
              <AlertDescription>
                Failed to load messages. Please try refreshing the chat.
              </AlertDescription>
            </Alert>
          )}

          {messagesLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingState description="Loading messages..." />
            </div>
          ) : (
            <ScrollArea 
              ref={scrollAreaRef}
              className="flex-1 px-4"
            >
              <div className="py-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-center">
                    <MessageCircle className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No messages yet. Start the conversation!
                    </p>
                  </div>
                ) : (
                  <>
                    {messages.map((message) => (
                      <ChatBubble
                        key={message.id}
                        message={message}
                        isOwnMessage={message.senderUserId === user.id}
                      />
                    ))}
                    {/* Scroll anchor */}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Input area */}
        <div className="flex-shrink-0 p-4 border-t bg-muted/30">
          <ChatInput
            value={newMessage}
            onChange={setNewMessage}
            onSend={handleSendMessage}
            disabled={operationsLoading || messagesLoading}
            placeholder={`Message ${otherUserName}...`}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
